import { api } from './apiClient';
import {
  Creator, Approval, ActivityEvent, SystemService, AppNotification,
  Partnership, PartnershipContact, LedgerEntry, TimelineEntry, Contract, Agent,
} from '@/types/entities';
import type { DemoSettings } from './demoDataService';

/**
 * LIVE API implementation — covers every module wired to the V8 backend:
 * Creators (+follow-ups), Approvals, Activity, System health,
 * Notifications, Partnerships (+contacts/timeline/ledger), Contracts
 * (+versioning), AI Agents (full CRUD), Connectors, Settings.
 * dataService.ts calls into this when DATA_MODE === 'live'.
 *
 * The interfaces below describe the JSON the backend actually sends (see
 * backend/src/routes/*.ts's toRow() functions) — kept separate from the
 * frontend's own Creator/Approval/etc. entity types since the wire shape
 * and the UI shape are allowed to diverge without either side needing a
 * blanket `any`.
 */
interface CreatorWire {
  id: string; fullName: string; tiktokUsername: string; tiktokUid: string | null;
  phone: string | null; whatsapp: string | null; email: string | null; country: string | null;
  emirate: string | null; language: string | null; source: string | null; joinDate: string | null;
  status: string; ownerId: string | null; riskLevel: string; nextFollowup: string | null;
  nextAction: string | null; notes: string | null; archived: boolean;
}
interface ApprovalWire {
  id: string; requestedBy: string; targetType: string; targetId: string | null;
  actionType: string; reason: string | null; payload: unknown; status: string;
  createdAt: string; reviewedAt: string | null; reviewedBy: string | null;
}
interface ActivityWire {
  id: string; actorType: 'agent' | 'user' | 'system'; actorLabel: string; action: string;
  targetEntityType?: string; targetEntityId?: string; result: 'good' | 'warn' | 'crit' | 'info'; createdAt: string;
}
interface SystemHealthWire {
  key: string; displayName: string; status: string; detail?: string;
}
interface PartnershipWire {
  id: string; partnerName: string; legalName: string | null; type: string | null;
  country: string | null; industry: string | null; status: string; website: string | null;
  notes: string | null; archived: boolean; createdAt: string; updatedAt: string;
  contacts: PartnershipContact[]; timeline: WireTimelineEntry[]; ledger: LedgerEntry[];
}
interface WireTimelineEntry { id: string; label: string; note: string | null; createdAt: string; actor: string }
interface ContractWire {
  id: string; partnershipId: string; title: string; version: string; status: string;
  effectiveDate: string | null; expiryDate: string | null; currency: string | null;
  contractValue: string | null; archived: boolean;
}
interface AgentWire {
  id: string; name: string; provider: string | null; model: string | null;
  permissionLevel: number; enabled: boolean; description: string | null;
  providerStatus: string; isPreviewCreated: boolean; archived: boolean;
}
interface ConnectorWire {
  key: string; displayName: string; category: string; status: string;
  label: string | null; notes: string | null; enabledUi: boolean;
}
interface SettingsWire {
  companyName: string; timezone: string; theme: string; density: string;
  sidebarCollapsed: boolean; notificationPrefs: Record<string, boolean>;
  creatorRules: { defaultFollowupDays: number; riskThresholdDays: number; defaultOwner: string };
  exportSettings: { defaultFileType: string; includeBranding: boolean; includeTimestamp: boolean };
}

export const liveDataService = {
  async listCreators(): Promise<Creator[]> {
    const { data } = await api.get<{ data: CreatorWire[] }>('/api/creators?pageSize=100');
    return data.map(mapCreator);
  },
  async createCreator(input: Partial<Creator> & { fullName: string; tiktokUsername: string }): Promise<Creator> {
    const created = await api.post<CreatorWire>('/api/creators', input);
    return mapCreator(created);
  },
  async updateCreator(id: string, patch: Partial<Creator>): Promise<Creator> {
    const updated = await api.patch<CreatorWire>(`/api/creators/${id}`, patch);
    return mapCreator(updated);
  },
  async archiveCreator(id: string): Promise<void> {
    await api.post(`/api/creators/${id}/archive`);
  },

  async listApprovals(): Promise<Approval[]> {
    const { data } = await api.get<{ data: ApprovalWire[] }>('/api/approvals');
    return data.map(mapApproval);
  },
  async decideApproval(id: string, decision: 'approved' | 'rejected'): Promise<void> {
    await api.post(`/api/approvals/${id}/decide`, { decision });
  },

  async listActivity(): Promise<ActivityEvent[]> {
    const { data } = await api.get<{ data: ActivityWire[] }>('/api/activity');
    return data.map((r) => ({
      id: r.id, actorType: r.actorType, actorLabel: r.actorLabel, action: r.action,
      targetEntityType: r.targetEntityType, targetEntityId: r.targetEntityId, result: r.result, createdAt: r.createdAt,
    }));
  },

  async listSystemServices(): Promise<SystemService[]> {
    const health = await api.get<{ services: SystemHealthWire[] }>('/api/system/health');
    return health.services.map((s) => ({ key: s.key, displayName: s.displayName, status: s.status, detail: s.detail }));
  },

  async listNotifications(): Promise<AppNotification[]> {
    const { data } = await api.get<{ data: AppNotification[] }>('/api/notifications');
    return data;
  },
  async markNotificationRead(id: string): Promise<void> { await api.post(`/api/notifications/${id}/read`); },
  async markAllNotificationsRead(): Promise<void> { await api.post('/api/notifications/read-all'); },
  async clearNotification(id: string): Promise<void> { await api.post(`/api/notifications/${id}/clear`); },

  // ---- Partnerships --------------------------------------------------
  async listPartnerships(): Promise<Partnership[]> {
    const { data } = await api.get<{ data: PartnershipWire[] }>('/api/partnerships');
    return data.map(mapPartnership);
  },
  async getPartnership(id: string): Promise<Partnership | undefined> {
    try { return mapPartnership(await api.get<PartnershipWire>(`/api/partnerships/${id}`)); }
    catch { return undefined; }
  },
  async createPartnership(input: Partial<Partnership> & { partnerName: string }): Promise<Partnership> {
    return mapPartnership(await api.post<PartnershipWire>('/api/partnerships', input));
  },
  async updatePartnership(id: string, patch: Partial<Partnership>): Promise<Partnership> {
    return mapPartnership(await api.patch<PartnershipWire>(`/api/partnerships/${id}`, patch));
  },
  async addPartnershipContact(id: string, contact: Omit<PartnershipContact, 'id'>): Promise<Partnership> {
    return mapPartnership(await api.post<PartnershipWire>(`/api/partnerships/${id}/contacts`, contact));
  },
  async addPartnershipTimelineEntry(id: string, label: string, note?: string): Promise<Partnership> {
    return mapPartnership(await api.post<PartnershipWire>(`/api/partnerships/${id}/timeline`, { label, note }));
  },
  async addPartnershipLedgerEntry(id: string, entry: Omit<LedgerEntry, 'id' | 'createdAt'>): Promise<Partnership> {
    return mapPartnership(await api.post<PartnershipWire>(`/api/partnerships/${id}/ledger`, entry));
  },
  async archivePartnership(id: string): Promise<void> { await api.post(`/api/partnerships/${id}/archive`); },

  // ---- Contracts --------------------------------------------------
  async listContracts(): Promise<Contract[]> {
    const { data } = await api.get<{ data: ContractWire[] }>('/api/contracts');
    return data.map(mapContract);
  },
  async createContract(input: Partial<Contract> & { partnershipId: string; title: string }): Promise<Contract> {
    return mapContract(await api.post<ContractWire>('/api/contracts', { ...input, partnershipDisplayId: input.partnershipId }));
  },
  async updateContract(id: string, patch: Partial<Contract>): Promise<Contract> {
    return mapContract(await api.patch<ContractWire>(`/api/contracts/${id}`, patch));
  },
  async duplicateContractVersion(id: string): Promise<Contract> {
    return mapContract(await api.post<ContractWire>(`/api/contracts/${id}/duplicate`));
  },
  async archiveContract(id: string): Promise<void> { await api.post(`/api/contracts/${id}/archive`); },

  // ---- AI Agents --------------------------------------------------
  async listAgents(): Promise<Agent[]> {
    const { data } = await api.get<{ data: AgentWire[] }>('/api/agents');
    return data.map(mapAgent);
  },
  async createAgent(input: Partial<Agent> & { name: string; provider: string; model: string }): Promise<Agent> {
    const key = input.id ?? input.name.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
    return mapAgent(await api.post<AgentWire>('/api/agents', { key, name: input.name, provider: input.provider, model: input.model, permissionLevel: input.permissionLevel, description: input.description }));
  },
  async updateAgent(id: string, patch: Partial<Agent>): Promise<Agent> {
    return mapAgent(await api.patch<AgentWire>(`/api/agents/${id}`, patch));
  },
  async toggleAgentPause(id: string): Promise<Agent> { return mapAgent(await api.post<AgentWire>(`/api/agents/${id}/pause`)); },
  async archiveAgent(id: string): Promise<void> { await api.post(`/api/agents/${id}/archive`); },
  async deleteAgent(id: string): Promise<void> { await api.delete(`/api/agents/${id}`); },

  // ---- Connectors --------------------------------------------------
  async listConnectorStatuses(): Promise<{ key: string; displayName: string; category: string; status: string; label?: string; enabledUi: boolean; notes?: string }[]> {
    const { data } = await api.get<{ data: ConnectorWire[] }>('/api/connectors');
    return data.map((c) => ({ key: c.key, displayName: c.displayName, category: c.category, status: c.status, label: c.label ?? undefined, enabledUi: c.enabledUi, notes: c.notes ?? undefined }));
  },
  async updateConnectorOverride(key: string, patch: { label?: string; enabledUi?: boolean; notes?: string }): Promise<void> {
    await api.patch(`/api/connectors/${key}`, patch);
  },

  // ---- Settings --------------------------------------------------
  async getSettings(): Promise<DemoSettings> {
    const s = await api.get<SettingsWire>('/api/settings');
    return mapSettings(s);
  },
  async updateSettings(patch: Partial<DemoSettings>): Promise<void> {
    await api.patch('/api/settings', patch);
  },
};

function mapCreator(r: CreatorWire): Creator {
  return {
    id: r.id, fullName: r.fullName, tiktokUsername: r.tiktokUsername, tiktokUid: r.tiktokUid ?? undefined,
    phone: r.phone ?? '', whatsapp: r.whatsapp ?? undefined, email: r.email ?? '', country: r.country ?? '',
    emirate: r.emirate ?? '', language: r.language ?? '', source: r.source ?? undefined, joinDate: r.joinDate ?? '',
    status: r.status, ownerId: r.ownerId ?? '', riskLevel: (r.riskLevel as Creator['riskLevel']) ?? 'low',
    nextFollowup: r.nextFollowup ?? undefined, nextAction: r.nextAction ?? undefined, notes: r.notes ?? undefined,
    timeline: [], archived: r.archived,
  };
}

function mapApproval(r: ApprovalWire): Approval {
  return {
    id: r.id, agentId: r.requestedBy, targetEntityType: r.targetType as Approval['targetEntityType'],
    targetEntityId: r.targetId ?? '', actionTitle: r.actionType, reason: r.reason ?? '',
    payload: JSON.stringify(r.payload ?? {}), permissionLevel: 2, status: r.status,
    createdAt: r.createdAt, decidedAt: r.reviewedAt ?? undefined, decidedBy: r.reviewedBy ?? undefined,
  };
}

function mapPartnership(r: PartnershipWire): Partnership {
  return {
    id: r.id, partnerName: r.partnerName, legalName: r.legalName ?? undefined, type: r.type ?? '',
    country: r.country ?? '', industry: r.industry ?? undefined, status: r.status,
    website: r.website ?? undefined, notes: r.notes ?? undefined, ownerId: '',
    contacts: r.contacts ?? [],
    timeline: (r.timeline ?? []).map((t): TimelineEntry => ({ id: t.id, label: t.label, note: t.note ?? undefined, createdAt: t.createdAt, actor: t.actor })),
    ledger: r.ledger ?? [], archived: r.archived,
  };
}

function mapContract(r: ContractWire): Contract {
  return {
    id: r.id, partnershipId: r.partnershipId, title: r.title, version: r.version, status: r.status,
    effectiveDate: r.effectiveDate ?? undefined, expiryDate: r.expiryDate ?? undefined,
    currency: r.currency ?? undefined, contractValue: r.contractValue ? Number(r.contractValue) : undefined,
    signedByUgarits: false, signedByPartner: false, hasSignedFile: false, archived: r.archived,
  };
}

function mapAgent(r: AgentWire): Agent {
  const status: Agent['status'] = !r.enabled
    ? 'paused'
    : r.providerStatus === 'connected' ? 'idle' : 'waiting_credentials';
  return {
    id: r.id, name: r.name, provider: r.provider ?? '', model: r.model ?? '',
    permissionLevel: (r.permissionLevel as 1 | 2 | 3) ?? 2, status,
    description: r.description ?? undefined, isPreviewCreated: r.isPreviewCreated, archived: r.archived,
  };
}

function mapSettings(s: SettingsWire): DemoSettings {
  // Phase C added a batch of new DemoSettings fields (branding, reduced
  // motion, date/number format, follow-up/approval/partnership/contract/
  // agent rule blocks) that the V8 backend's /api/settings contract does
  // not carry yet — SettingsWire above is unchanged. Rather than break
  // the live-mode build, every new field gets an honest local default
  // here, same pattern as the pre-existing defaultLandingRoute fallback.
  // Once the backend contract grows these columns, replace the literal
  // with `s.<field>` the same way the rest of this function does.
  return {
    companyName: s.companyName,
    legalName: s.companyName, // not tracked separately server-side in V8 yet
    defaultLandingRoute: '/', // client-side routing preference — not tracked server-side in V8
    timezone: s.timezone,
    defaultCurrency: 'AED', // not tracked server-side in V8 yet
    branding: { appName: 'Ugarits Command', brandColor: '#c9932a', hasLogo: false }, // not tracked server-side in V8 yet
    theme: s.theme === 'dark' ? 'dark' : 'light',
    density: s.density === 'compact' ? 'compact' : 'comfortable',
    sidebarCollapsed: s.sidebarCollapsed,
    reducedMotion: false, // not tracked server-side in V8 yet
    dateFormat: 'YYYY-MM-DD', // not tracked server-side in V8 yet
    numberFormat: 'western', // not tracked server-side in V8 yet
    notificationPrefs: {
      followupDue: s.notificationPrefs.followupDue ?? true,
      approvalWaiting: s.notificationPrefs.approvalWaiting ?? true,
      creatorAtRisk: s.notificationPrefs.creatorAtRisk ?? true,
      contractExpiring: s.notificationPrefs.contractExpiring ?? true,
      systemAlerts: s.notificationPrefs.systemAlerts ?? true,
    },
    creatorRules: { ...s.creatorRules, idPrefix: 'UGC' }, // idPrefix not tracked server-side in V8 yet
    followupRules: { defaultPriority: 'medium', overdueBehavior: 'flag', reminderEnabled: true }, // not tracked server-side in V8 yet
    approvalRules: { defaultPermissionLevel: 2, expirationDays: 7, highRiskConfirm: true }, // not tracked server-side in V8 yet
    partnershipRules: { idPrefix: 'UG-PART', defaultCurrency: 'AED', renewalWarningDays: 30 }, // not tracked server-side in V8 yet
    contractRules: { idPrefix: 'UG-CON', expiryWarningDays: 30, renewalWarningDays: 30, defaultNoticePeriodDays: 30 }, // not tracked server-side in V8 yet
    agentRules: { defaultPermissionLevel: 2 }, // not tracked server-side in V8 yet
    automationRules: { defaultApprovalLevel: 2 }, // not tracked server-side in V8 yet
    supportRules: { diagnosticsReminderEnabled: false }, // not tracked server-side in V8 yet
    exportSettings: {
      defaultFileType: s.exportSettings.defaultFileType === 'pdf' ? 'pdf' : 'csv',
      includeBranding: s.exportSettings.includeBranding,
      includeTimestamp: s.exportSettings.includeTimestamp,
      fileNamePrefix: 'ugarits', // not tracked server-side in V8 yet
    },
  };
}
